import { useIsInsideRoom, useStatus, useSyncStatus } from './liveblocks-notes.config'
import {
  BoldIcon,
  CircleCheckIcon,
  Heading1Icon,
  Heading2Icon,
  Heading3Icon,
  ImageIcon,
  ItalicIcon,
  LinkIcon,
  ListChecksIcon,
  ListIcon,
  ListOrderedIcon,
  Loader2Icon,
  StrikethroughIcon,
  UnderlineIcon,
  XCircleIcon,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { RangeStatic } from 'react-quill-new'

import { cn } from '@/lib/ui/style-helpers'

import { Badge, BadgeProps } from '@/components/lemmala-design-system/badge/badge'
import { Button } from '@/components/lemmala-design-system/button/button'

import { LiveblocksAvatars } from './liveblocks-avatars'
import { QuillEditorType } from './rich-text-editor'
import { toolbarButtonVariant } from './variants'

type Props = {
  getQuill: () => QuillEditorType | null
  uploadImage: () => void
  onFormatApply?: (format: string) => void
}

type FormatValue = boolean | string | unknown

type ConnectionStatus = 'disconnected' | 'syncing' | 'synced'

const ConnectionStatusBadge = ({ status }: { status: ConnectionStatus }) => {
  const variantMap: Record<ConnectionStatus, BadgeProps['variant']> = {
    disconnected: 'danger',
    syncing: 'neutral',
    synced: 'success',
  }
  const iconMap = {
    disconnected: <XCircleIcon size={14} />,
    syncing: <Loader2Icon className='animate-spin' size={14} />,
    synced: <CircleCheckIcon size={14} />,
  }

  return (
    <Badge variant={variantMap[status]} badgeStyle='subtle' className='h-6 xl:gap-1'>
      {iconMap[status]}
      <span className='hidden xl:inline'>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
    </Badge>
  )
}

// Component that renders connection info - only renders inside a Liveblocks room
function LiveblocksConnectionInfo() {
  const status = useStatus()
  const syncStatus = useSyncStatus({ smooth: true })

  const connectionStatus = useMemo(() => {
    switch (status) {
      case 'connecting':
      case 'initial':
      case 'reconnecting':
        return 'syncing'
      case 'connected':
        return syncStatus === 'synchronized' ? 'synced' : 'syncing'
      default:
        return 'disconnected'
    }
  }, [status, syncStatus])

  return (
    <div className='connection-info flex grow items-center justify-end gap-2.5 p-4'>
      <ConnectionStatusBadge status={connectionStatus} />
      <LiveblocksAvatars />
    </div>
  )
}

export function Toolbar({ getQuill, uploadImage, onFormatApply }: Props) {
  const [selectionFormat, setSelectionFormat] = useState<Record<string, FormatValue>>({})
  const isInsideRoom = useIsInsideRoom()

  useEffect(() => {
    const quill = getQuill()
    if (!quill) {
      return
    }

    const handleSelectionChange = (range: RangeStatic | null) => {
      if (range) {
        const selectionFormat = quill.getFormat(range.index, range.length)
        setSelectionFormat(selectionFormat)
      } else {
        setSelectionFormat({})
      }
    }

    quill.on('selection-change', handleSelectionChange)
  }, [getQuill])

  return (
    <div className='flex !border-none' id='quill-toolbar'>
      <div className='flex items-center gap-2.5 overflow-x-auto p-4 pl-1'>
        <div className='flex gap-1'>
          <ToolbarButton
            format='header'
            value={1}
            selectionFormat={selectionFormat}
            getQuill={getQuill}
            onFormatApply={onFormatApply}
          />
          <ToolbarButton
            format='header'
            value={2}
            selectionFormat={selectionFormat}
            getQuill={getQuill}
            onFormatApply={onFormatApply}
          />
          <ToolbarButton
            format='header'
            value={3}
            selectionFormat={selectionFormat}
            getQuill={getQuill}
            onFormatApply={onFormatApply}
          />
        </div>
        <div className='flex gap-1'>
          <ToolbarButton
            format='bold'
            selectionFormat={selectionFormat}
            getQuill={getQuill}
            onFormatApply={onFormatApply}
          />
          <ToolbarButton
            format='italic'
            selectionFormat={selectionFormat}
            getQuill={getQuill}
            onFormatApply={onFormatApply}
          />
          <ToolbarButton
            format='underline'
            selectionFormat={selectionFormat}
            getQuill={getQuill}
            onFormatApply={onFormatApply}
          />
        </div>
        <div className='flex gap-1'>
          <ToolbarButton
            format='list'
            value='ordered'
            selectionFormat={selectionFormat}
            getQuill={getQuill}
            onFormatApply={onFormatApply}
          />
          <ToolbarButton
            format='list'
            value='bullet'
            selectionFormat={selectionFormat}
            getQuill={getQuill}
            onFormatApply={onFormatApply}
          />
          <ToolbarButton
            format='list'
            value='check'
            selectionFormat={selectionFormat}
            getQuill={getQuill}
            onFormatApply={onFormatApply}
          />
        </div>
        <div className='flex gap-1'>
          <ToolbarButton
            format='image'
            selectionFormat={selectionFormat}
            getQuill={getQuill}
            uploadImage={uploadImage}
            onFormatApply={onFormatApply}
          />
          <ToolbarButton
            format='link'
            selectionFormat={selectionFormat}
            getQuill={getQuill}
            onFormatApply={onFormatApply}
          />
        </div>
      </div>
      {isInsideRoom && <LiveblocksConnectionInfo />}
    </div>
  )
}
type ToolbarButtonProps = {
  selectionFormat: Record<string, FormatValue>
  format: 'bold' | 'italic' | 'underline' | 'strike' | 'list' | 'image' | 'link' | 'header'
  value?: string | number
  getQuill: () => QuillEditorType | null
  uploadImage?: () => void
  onFormatApply?: (format: string) => void
}

const getTrackingFormatName = (
  format: ToolbarButtonProps['format'],
  value?: ToolbarButtonProps['value'],
) => {
  if (format === 'header') {
    return /[1-6]/.test(String(value)) ? `h${value}` : null
  }

  if (format === 'list') {
    return `${value}-list`
  }

  return format
}

const ToolbarButton = ({
  format,
  value,
  selectionFormat,
  getQuill,
  uploadImage,
  onFormatApply,
}: ToolbarButtonProps) => {
  const active = useMemo(() => {
    switch (format) {
      case 'link':
        return !!selectionFormat[format]
      case 'list':
        if (value === 'check') {
          return ['checked', 'unchecked'].includes(selectionFormat[format] as string)
        }
        return selectionFormat[format] === value
      default:
        if (!value) {
          return selectionFormat[format] === true
        }
        return selectionFormat[format] === value
    }
  }, [format, selectionFormat, value])

  const getIcon = () => {
    switch (format) {
      case 'bold':
        return <BoldIcon size={14} />
      case 'italic':
        return <ItalicIcon size={14} />
      case 'underline':
        return <UnderlineIcon size={14} />
      case 'strike':
        return <StrikethroughIcon size={14} />
      case 'header':
        switch (value) {
          case 1:
            return <Heading1Icon size={14} />
          case 2:
            return <Heading2Icon size={14} />
          case 3:
            return <Heading3Icon size={14} />
        }
        break
      case 'list':
        switch (value) {
          case 'ordered':
            return <ListOrderedIcon size={14} />
          case 'bullet':
            return <ListIcon size={14} />
          case 'check':
            return <ListChecksIcon size={14} />
        }
        break
      case 'link':
        return <LinkIcon size={14} />
      case 'image':
        return <ImageIcon size={14} />
    }
    return null
  }

  return (
    <Button
      variant='ghost'
      size='icon'
      className={cn(`ql-${format}`, toolbarButtonVariant({ active }), active && 'ql-active')}
      value={value}
      onClick={() => {
        const quill = getQuill()
        if (!quill) return

        const trackingFormat = getTrackingFormatName(format, value)
        if (trackingFormat) {
          onFormatApply?.(trackingFormat)
        }

        const range = quill.getSelection(true)
        if (!range) return

        const currentFormat = quill.getFormat(range.index, range.length)
        let nextValue

        // For special formats, use Quill's native handlers
        if (format === 'image') {
          if (uploadImage) {
            uploadImage()
          }
          return
        }

        if (format === 'link') {
          // Use Quill's native link handler now that toolbar module is available
          const toolbar = quill.getModule('toolbar') as {
            handlers: { link: (link: boolean | string) => void }
          }
          if (toolbar && toolbar.handlers && toolbar.handlers.link) {
            const existingLink = currentFormat.link as string | undefined
            toolbar.handlers.link.call(toolbar, !existingLink)
          } else {
            // Fallback to simple toggle
            const existingLink = currentFormat.link as string | undefined
            quill.format('link', !existingLink, 'user')
          }
          return
        }

        if (format === 'list' && value === 'check') {
          // Use Quill's native list handler
          const toolbar = quill.getModule('toolbar') as {
            handlers: { list: (list: string) => void }
          }
          if (toolbar && toolbar.handlers && toolbar.handlers.list) {
            toolbar.handlers.list.call(toolbar, value)
          } else {
            // Fallback to direct format
            const currentList = currentFormat.list as string | undefined
            const nextValue = currentList === 'check' ? false : 'check'
            quill.format('list', nextValue, 'user')
          }
          return
        }

        // For other formats, use our custom toggle logic
        switch (format) {
          case 'header':
            nextValue = currentFormat.header === value ? false : value
            break
          case 'list':
            nextValue = currentFormat.list === value ? false : value
            break
          case 'bold':
          case 'italic':
          case 'underline':
          case 'strike':
            nextValue = currentFormat[format] === true ? false : true
            break
          default:
            return
        }

        quill.format(format, nextValue, 'user')
      }}
      onMouseDown={(event) => event.preventDefault()}
    >
      {getIcon()}
    </Button>
  )
}
